import { clearToken, getInitData, getLaunchDiag, readToken, writeToken } from './lib/twa.js';

let reauthInFlight = null;

function joinUrl(base, path) {
  if (!base) return path;
  return base.replace(/\/+$/, '') + path;
}

function buildApiBase() {
  if (typeof import.meta !== 'undefined' && import.meta.env?.VITE_API_BASE) {
    return import.meta.env.VITE_API_BASE;
  }
  return '';
}

export async function apiRequest(method, urlPath, body, { allowReauth = true } = {}) {
  const base = buildApiBase();
  const url = joinUrl(base, urlPath);
  let token = await readToken();
  const headers = { 'content-type': 'application/json' };
  if (token) headers.authorization = `Bearer ${token}`;

  const res = await fetch(url, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (res.status === 401 && allowReauth) {
    await clearToken();
    const newToken = await reauth();
    if (newToken) {
      return apiRequest(method, urlPath, body, { allowReauth: false });
    }
  }

  const text = await res.text();
  let payload = null;
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      payload = { error: { code: 'INTERNAL', message: text.slice(0, 200) } };
    }
  }
  if (!res.ok) {
    const err = new Error(payload?.error?.message || `HTTP ${res.status}`);
    err.code = payload?.error?.code || 'INTERNAL';
    err.status = res.status;
    err.payload = payload;
    throw err;
  }
  return payload;
}

export async function reauth({ launchChatId } = {}) {
  if (reauthInFlight) return reauthInFlight;
  const initData = getInitData();
  if (!initData) {
    reauthInFlight = null;
    try {
      const diag = getLaunchDiag();
      const base = buildApiBase();
      await fetch(joinUrl(base, '/api/_diag'), {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ reason: 'empty_initdata', launchChatId: launchChatId || null, ...diag }),
      });
    } catch {
      /* diagnostics are best-effort */
    }
    return null;
  }
  reauthInFlight = (async () => {
    try {
      const base = buildApiBase();
      const url = joinUrl(base, '/api/auth');
      const body = { initData };
      if (launchChatId) body.launch_chat_id = launchChatId;
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
      });
      const text = await res.text();
      let payload = null;
      try {
        payload = text ? JSON.parse(text) : null;
      } catch {
        const err = new Error(
          `Backend unreachable (HTTP ${res.status}). Try again in a moment.`
        );
        err.code = 'BACKEND_UNREACHABLE';
        err.status = res.status;
        throw err;
      }
      if (!res.ok) {
        const err = new Error(payload?.error?.message || 'auth failed');
        err.code = payload?.error?.code || 'AUTH_FAILED';
        err.status = res.status;
        throw err;
      }
      if (payload?.token) {
        await writeToken(payload.token);
      }
      return payload;
    } catch (e) {
      await clearToken();
      throw e;
    } finally {
      reauthInFlight = null;
    }
  })();
  return reauthInFlight;
}

export async function auth({ launchChatId } = {}) {
  return reauth({ launchChatId });
}

export async function listEvents(chatId, params = {}) {
  const qs = new URLSearchParams();
  if (params.from) qs.set('from', params.from);
  if (params.to) qs.set('to', params.to);
  if (params.archived) qs.set('archived', 'true');
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return apiRequest('GET', `/api/chats/${chatId}/events${suffix}`);
}

export async function createEvent(chatId, body) {
  return apiRequest('POST', `/api/chats/${chatId}/events`, body);
}

export async function getEvent(id) {
  return apiRequest('GET', `/api/events/${id}`);
}

export async function updateEvent(id, body) {
  return apiRequest('PATCH', `/api/events/${id}`, body);
}

export async function cancelEvent(id, reason) {
  return apiRequest('DELETE', `/api/events/${id}`, { reason });
}

export async function setRsvp(id, status, reason) {
  const body = { status };
  if (reason) body.reason = reason;
  return apiRequest('POST', `/api/events/${id}/rsvp`, body);
}

export async function clearRsvp(id) {
  return apiRequest('DELETE', `/api/events/${id}/rsvp`);
}

export async function clearSession() {
  await clearToken();
}

export async function getMe() {
  return apiRequest('GET', '/api/me');
}

export async function getBotInfo() {
  return apiRequest('GET', '/api/bot-info', undefined, { allowReauth: false });
}
